import 'url-search-params-polyfill';
import * as React from 'react';
import ReactDOM from 'react-dom';
import {
  createStore, combineReducers, applyMiddleware, compose
} from 'redux';
import { Provider as ReduxProvider } from 'react-redux';
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloProvider,
  ApolloLink
} from '@apollo/client';
import thunk from 'redux-thunk';
import { createUploadLink } from 'apollo-upload-client';
import App from '~/app/components/App';
import { GENESIS_FLAG } from 'shared/lib/genesisOrigin';

// The admin runs with people's sessions: sections an AI app wrote don't run their scripts here (shared/lib/genesisOrigin).
(window as unknown as Record<string, unknown>)[GENESIS_FLAG] = true;
import { startThemeSync } from '~/app/ui/theme';
import appReducer from '~/app/reducers';
import pagesReducer from '~/pages/reducers';
import categoriesReducer from '~/categories/reducers';
import reportsReducer from '~/reports/reducers';
import rolesReducer from '~/roles/reducers';
import templatesReducer from '~/templates/reducers';
import { DefaultThemeProvider } from '@ff/ui';

declare global {
  interface Window {
    __PRELOADED_STATE__: any,
    __REDUX_DEVTOOLS_EXTENSION__: any
  }
}

/* =============================================================================
=    Redux
============================================================================= */
const reducer = combineReducers({
  app: appReducer,
  pages: pagesReducer,
  categories: categoriesReducer,
  reports: reportsReducer,
  roles: rolesReducer,
  templates: templatesReducer
});
const initialState = window.__PRELOADED_STATE__;

// delete window.__PRELOADED_STATE__;

const enhancer = compose(
  applyMiddleware(
    thunk
  ),
  // eslint-disable-next-line
  (typeof window.__REDUX_DEVTOOLS_EXTENSION__ !== 'undefined') ? window.__REDUX_DEVTOOLS_EXTENSION__() : f => f
);
const store = createStore(reducer, initialState, enhancer);

/* =============================================================================
=    Apollo
============================================================================= */

const httpLinkOptions = { uri: '/graphql', credentials: 'same-origin' };
// Preflight header required to for CSRF prevention compatibility
const uploadLinkOptions = { uri: '/graphql', credentials: 'same-origin', headers: { 'Apollo-Require-Preflight': true }};
const link = ApolloLink.split(
  operation => operation.operationName === 'uploadFiles',
  createUploadLink(uploadLinkOptions),
  createHttpLink(httpLinkOptions)
);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache()
});

const app = (
  // ThemeProvider here provides theme for widgets in Editor, Widget Presets
  <DefaultThemeProvider customizedTheme={{ allowHtmlText: true }}>
    <ApolloProvider client={client}>
      <ReduxProvider store={store}>
        <App />
      </ReduxProvider>
    </ApolloProvider>
  </DefaultThemeProvider>
);
const rootElement = document.getElementById('app');

// Light or dark, as chosen in this browser (the page head already applied it before loading).
startThemeSync();
ReactDOM.render(app, rootElement);
